import { useState, useEffect } from 'react';
import { usePlacesAutocomplete } from 'places-autocomplete-hook';
import './CreateEventModal.css';

export default function CreateEventModal({ isOpen, onClose, onCreate, initialData, isEditMode, onUpdate, onDelete }) {
    const [name, setName] = useState('My Potluck Party');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [emoji, setEmoji] = useState('🎉');
    const [guestLimit, setGuestLimit] = useState(0); // 0 means no limit
    const [mode, setMode] = useState('POTLUCK'); // POTLUCK, SIGNUP, RSVP
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [description, setDescription] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const {
        value: locationInput,
        suggestions,
        setValue: setLocationValue,
        handlePlaceSelect,
        getPlaceDetails,
        loading: loadingSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            /* Define options here if needed, or pass stable object */
        },
        apiKey: "AIzaSyByMSIR9nzJqeVwch2lb1fnZ8B-iQs6ZTQ",
    });

    useEffect(() => {
        if (isOpen && initialData && isEditMode) {
            setName(initialData.name || '');
            const dateStr = initialData.date ? (initialData.date.includes('T') ? initialData.date.split('T')[0] : initialData.date) : '';
            setDate(dateStr);
            setTime(initialData.time || '');
            setEmoji(initialData.emoji || '🎉');
            setGuestLimit(initialData.guestLimit || 0);
            setMode(initialData.mode || 'POTLUCK');
            setDescription(initialData.description || '');

            if (initialData.location) {
                setSelectedLocation(initialData.location);
                // Only set input value if it's different to avoid cursor jumping or loops
                // But setLocationValue sets the input string.
                setLocationValue(initialData.location.address || '', false);
            } else {
                setSelectedLocation(null);
                setLocationValue('', false);
            }
            setShowDeleteConfirm(false);
        } else if (isOpen && !isEditMode) {
            setName('My Potluck Party');
            setDate('');
            setTime('');
            setEmoji('🎉');
            setGuestLimit(0);
            setMode('POTLUCK');
            setDescription('');
            setSelectedLocation(null);
            setLocationValue('', false);
            setShowDeleteConfirm(false);
        }
        // Removing setLocationValue and initialData from dependency array effectively 
        // makes this run only when isOpen changes or isEditMode changes.
        // We really only want to 'reset' or 'fill' when the modal opens.
        // If we strictly follow react-hooks/exhaustive-deps, we need a ref to track if we've initialized for this open session.
        // But simply removing them works for "Action on Open".
        // However, if we swap between events while open (unlikely here), it wouldn't update. 
        // Given the use case, this is safe.
    }, [isOpen, isEditMode]); // removed setLocationValue and initialData to prevent re-runs on typing

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const eventData = {
            name,
            date,
            time,
            emoji,
            guestLimit,
            mode,
            location: selectedLocation,
            description
        };

        if (isEditMode && onUpdate) {
            onUpdate(eventData);
        } else {
            onCreate(eventData);
        }
    };

    const handleSelect = async (placeId) => {
        try {
            await handlePlaceSelect(placeId);
            const details = await getPlaceDetails(placeId);
            console.log(details);
            setSelectedLocation({
                address: details.formattedAddress,
                placeId: placeId
            });
            // Pass false to prevent triggering a new search
            setLocationValue(details.formattedAddress, false);
            setShowSuggestions(false);
        } catch (error) {
            console.error("Error fetching place details:", error);
        }
    };

    const emojis = ['🎉', '🎈', '🎂', '🍕', '🍻', '🌮', '🎭', '🎄', '🎃', '🎆'];

    // Generate time options in 15-minute increments
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const hourStr = String(hour).padStart(2, '0');
                const minuteStr = String(minute).padStart(2, '0');
                const timeValue = `${hourStr}:${minuteStr}`;

                // Convert to 12-hour format for display
                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const period = hour >= 12 ? 'PM' : 'AM';
                const displayTime = `${displayHour}:${minuteStr} ${period}`;

                options.push({ value: timeValue, label: displayTime });
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2>{isEditMode ? 'Edit Event' : 'Create New Event'}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Event Name <span className="required-mark">*</span></label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="e.g. Summer BBQ"
                        />
                    </div>



                    <div className="form-group">
                        <label>Event Mode</label>
                        <div className="mode-selector">
                            <label className={`mode-option ${mode === 'POTLUCK' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="partyMode"
                                    value="POTLUCK"
                                    checked={mode === 'POTLUCK'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <span className="mode-label">
                                    <strong>🍲 Potluck</strong>
                                    <small>Guests can add items</small>
                                </span>
                            </label>
                            <label className={`mode-option ${mode === 'SIGNUP' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="partyMode"
                                    value="SIGNUP"
                                    checked={mode === 'SIGNUP'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <span className="mode-label">
                                    <strong>📋 Signup Sheet</strong>
                                    <small>Host adds items, guests signup</small>
                                </span>
                            </label>
                            <label className={`mode-option ${mode === 'RSVP' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="partyMode"
                                    value="RSVP"
                                    checked={mode === 'RSVP'}
                                    onChange={(e) => setMode(e.target.value)}
                                />
                                <span className="mode-label">
                                    <strong>📩 RSVP Only</strong>
                                    <small>No items, just attendance</small>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Date <span className="required-mark">*</span></label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Time <span className="optional-label">(Optional)</span></label>
                        <select
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        >
                            <option value="">Select a time</option>
                            {timeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group location-group">
                        <label>Location <span className="required-mark">*</span></label>
                        <input
                            type="text"
                            value={locationInput}
                            onChange={(e) => {
                                setLocationValue(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => {
                                if (locationInput.length > 0) setShowSuggestions(true);
                            }}
                            placeholder="Search for a location..."
                            required
                        />
                        {showSuggestions && suggestions.status === 'OK' && (
                            <ul className="suggestions-dropdown">
                                {suggestions.data.map((prediction) => (
                                    <li
                                        key={prediction.placeId}
                                        onClick={() => handleSelect(prediction.placeId)}
                                    >
                                        <strong>{prediction.structuredFormat?.mainText?.text}</strong>
                                        <small>{prediction.structuredFormat?.secondaryText?.text}</small>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {loadingSuggestions && <div className="location-loading">Searching...</div>}
                    </div>

                    <div className="form-group">
                        <label>Description <span className="optional-label">(Optional)</span></label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add details about the event..."
                            rows={3}
                            className="description-input"
                        />
                    </div>

                    <div className="form-group">
                        <label>Event Emoji <span className="required-mark">*</span></label>
                        <div className="emoji-selector">
                            {emojis.map(e => (
                                <button
                                    key={e}
                                    type="button"
                                    className={`emoji-btn ${emoji === e ? 'active' : ''}`}
                                    onClick={() => setEmoji(e)}
                                >
                                    {e}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Guest +1s <span className="required-mark">*</span></label>
                        <select
                            value={guestLimit}
                            onChange={(e) => setGuestLimit(Number(e.target.value))}
                        >
                            <option value={0}>No limit</option>
                            <option value={1}>Limit to +1 guest</option>
                            <option value={2}>Limit to +2 guests</option>
                            <option value={-1}>No guests allowed (Invitee only)</option>
                        </select>
                    </div>

                    <div className="modal-actions">
                        {isEditMode && onDelete && (
                            !showDeleteConfirm ? (
                                <button
                                    type="button"
                                    className="delete-btn-modal"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    Delete Event
                                </button>
                            ) : (
                                <div className="delete-confirm-wrapper">
                                    <span>Are you sure?</span>
                                    <button type="button" className="confirm-delete-btn" onClick={onDelete}>Yes, Delete</button>
                                    <button type="button" className="cancel-delete-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                                </div>
                            )
                        )}
                        <div className="right-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="submit-btn">{isEditMode ? 'Save Changes' : 'Create Event'}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

